import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { bool, isObject, isNumber, Cast, isString, isFunction, keys, isNullOrUndefined, findMatches } from '@remirror/core-helpers';
import { lift, wrapIn, setBlockType } from 'prosemirror-commands';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import _construct from '@babel/runtime/helpers/construct';
import { EMPTY_PARAGRAPH_NODE } from '@remirror/core-constants';
import minDocument from 'min-document';
import { NodeType, MarkType, Node as Node$1, Schema, ResolvedPos, Fragment, DOMSerializer, DOMParser, Slice } from 'prosemirror-model';
import { EditorState, TextSelection, Selection, NodeSelection, Plugin } from 'prosemirror-state';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { InputRule } from 'prosemirror-inputrules';

/// <reference types="node" />
/**
 * A object with flags identifying the current environment.
 *
 * @public
 */

var environment = {
  /**
   * Verifies that the environment has both a window and window.document
   */
  get isBrowser() {
    return bool(typeof window !== 'undefined' && typeof window.document !== 'undefined' && window.navigator && window.navigator.userAgent);
  },

  /**
   * Verifies that the environment is JSDOM
   */
  get isJSDOM() {
    return environment.isBrowser && window.navigator.userAgent.includes('jsdom');
  },

  /**
   * Verifies that the environment has a nodejs process and is therefore a node environment
   */
  get isNode() {
    return typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
  },

  /**
   * True when running on macOS
   */
  get isMac() {
    return environment.isBrowser && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  },

  /**
   * Verify that this is an apple device either on the client or server.
   */
  get isApple() {
    return environment.isNode ? process.platform === 'darwin' : environment.isBrowser ? /Mac|iPod|iPhone|iPad/.test(window.navigator.platform) : false;
  },

  /**
   * True when running in DEVELOPMENT environment
   */
  get isDevelopment() {
    return process.env.NODE_ENV === 'development';
  },

  /**
   * True when running unit tests
   */
  get isTest() {
    return process.env.NODE_ENV === 'test';
  },

  /**
   * True when running in PRODUCTION environment
   */
  get isProduction() {
    return process.env.NODE_ENV === 'production';
  }

};

/**
 * Check to see if the passed value is a NodeType.
 *
 * @param value - the value to check
 *
 * @public
 */

var isNodeType = function isNodeType(value) {
  return isObject(value) && value instanceof NodeType;
};
/**
 * Check to see if the passed value is a MarkType.
 *
 * @param value - the value to check
 *
 * @public
 */

var isMarkType = function isMarkType(value) {
  return isObject(value) && value instanceof MarkType;
};
/**
 * Checks to see if the passed value is a ProsemirrorNode
 *
 * @param value - the value to check
 *
 * @public
 */

var isProsemirrorNode = function isProsemirrorNode(value) {
  return isObject(value) && value instanceof Node$1;
};
/**
 * Checks to see if the passed value is a Prosemirror Editor State
 *
 * @param value - the value to check
 *
 * @public
 */

var isEditorState = function isEditorState(value) {
  return isObject(value) && value instanceof EditorState;
};
/**
 * Checks to see if the passed value is an instance of the editor schema
 *
 * @param value - the value to check
 *
 * @public
 */

var isEditorSchema = function isEditorSchema(value) {
  return isObject(value) && value instanceof Schema;
};
/**
 * Predicate checking whether the selection is a TextSelection
 *
 * @param value - the value to check
 *
 * @public
 */

var isTextSelection = function isTextSelection(value) {
  return isObject(value) && value instanceof TextSelection;
};
/**
 * Predicate checking whether the value is a Selection
 *
 * @param value - the value to check
 *
 * @public
 */

var isSelection = function isSelection(value) {
  return isObject(value) && value instanceof Selection;
};
/**
 * Predicate checking whether the value is a ResolvedPosition.
 *
 * @param value - the value to check
 *
 * @public
 */

var isResolvedPos = function isResolvedPos(value) {
  return isObject(value) && value instanceof ResolvedPos;
};
/**
 * Predicate checking whether the selection is a NodeSelection
 *
 * @param value - the value to check
 *
 * @public
 */

var isNodeSelection = function isNodeSelection(value) {
  return isObject(value) && value instanceof NodeSelection;
};

/**
 * Checks that a mark is active within the selected region, or the current selection point is within a
 * region with the mark active. Used by extensions to implement their active methods.
 *
 * @remarks
 * "Borrowed" from [tiptap](https://github.com/scrumpy/tiptap)
 *
 * @param state - the editor state
 * @param type - the mark type
 *
 * @public
 */
var isMarkActive = function isMarkActive(_ref) {
  var state = _ref.state,
      type = _ref.type,
      from = _ref.from,
      to = _ref.to;
  var selection = state.selection,
      doc = state.doc,
      storedMarks = state.storedMarks;
  var $from = selection.$from,
      empty = selection.empty;

  if (from && to) {
    try {
      return Math.max(from, to) < doc.nodeSize && doc.rangeHasMark(from, to, type);
    } catch (_unused) {
      return false;
    }
  }

  if (empty) {
    return bool(type.isInSet(storedMarks || $from.marks()));
  }

  return doc.rangeHasMark(selection.from, selection.to, type);
};
/**
 * Check if the specified type (NodeType) can be inserted at the current selection point.
 *
 * @remarks
 *
 * "Borrowed" from [tiptap](https://github.com/scrumpy/tiptap)
 *
 * @param state - the editor state
 * @param type - the node type
 *
 * @public
 */

var canInsertNode = function canInsertNode(state, type) {
  var $from = state.selection.$from;

  for (var depth = $from.depth; depth >= 0; depth--) {
    var index = $from.index(depth);

    try {
      if ($from.node(depth).canReplaceWith(index, index, type)) {
        return true;
      }
    } catch (_unused2) {
      return false;
    }
  }

  return false;
};
/**
 * Checks if a node looks like an empty document
 *
 * @param node - the prosemirror node
 *
 * @public
 */

var isDocNodeEmpty = function isDocNodeEmpty(node) {
  var nodeChild = node.content.firstChild;

  if (node.childCount !== 1 || !nodeChild) {
    return false;
  }

  return nodeChild.type.isBlock && !nodeChild.childCount && nodeChild.nodeSize === 2 && (!nodeChild.marks || nodeChild.marks.length === 0);
};
/**
 * Checks if the current node a paragraph node and empty
 *
 * @param node - the prosemirror node
 *
 * @public
 */

var isEmptyParagraphNode = function isEmptyParagraphNode(node) {
  return !isProsemirrorNode(node) || node.type.name === 'paragraph' && !node.textContent && !node.childCount;
};
/**
 * Retrieve the attributes for a mark.
 *
 * "Borrowed" from [tiptap](https://github.com/scrumpy/tiptap)
 *
 * @param state - the editor state
 * @param type - the mark type
 */

var getMarkAttrs = function getMarkAttrs(state, type) {
  var _state$selection = state.selection,
      from = _state$selection.from,
      to = _state$selection.to;
  var marks = [];
  state.doc.nodesBetween(from, to, function (node) {
    marks = [].concat(_toConsumableArray(marks), _toConsumableArray(node.marks));
  });
  var mark = marks.find(function (markItem) {
    return markItem.type.name === type.name;
  });

  if (mark) {
    return mark.attrs;
  }

  return {};
};
/**
 * Retrieve the start and end position of a mark
 *
 * @remarks
 *
 * "Borrowed" from [tiptap](https://github.com/scrumpy/tiptap)
 *
 * @param pmPosition - the resolved prosemirror position
 * @param type - the mark type
 */

var getMarkRange = function getMarkRange() {
  var pmPosition = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  if (!pmPosition || !type) {
    return false;
  }

  var start = pmPosition.parent.childAfter(pmPosition.parentOffset);

  if (!start.node) {
    return false;
  }

  var mark = start.node.marks.find(function (_ref2) {
    var markType = _ref2.type;
    return markType === type;
  });

  if (!mark) {
    return false;
  }

  var startIndex = pmPosition.index();
  var startPos = pmPosition.start() + start.offset;

  while (startIndex > 0 && mark.isInSet(pmPosition.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= pmPosition.parent.child(startIndex).nodeSize;
  }

  var endPos = startPos + start.node.nodeSize;
  return {
    from: startPos,
    to: endPos
  };
};
/**
 * Retrieves the text content from a slice
 *
 * @remarks
 * A utility that's useful for pulling text content from a slice which is usually created via `selection.content()`
 *
 * @param slice - the prosemirror slice
 *
 * @public
 */

var getTextContentFromSlice = function getTextContentFromSlice(slice) {
  var node = slice.content.firstChild;
  return node ? node.textContent : '';
};
/**
 * Takes an empty selection and expands it out to the nearest group not matching the excluded characters.
 *
 * @remarks
 *
 * Can be used to find the nearest selected word. See {@link getSelectedWord}
 *
 * @param state - the editor state
 * @param exclude - the regex pattern to exclude
 * @returns false if not a text selection or if no expansion available
 *
 * @public
 */

var getSelectedGroup = function getSelectedGroup(state, exclude) {
  if (!isTextSelection(state.selection)) {
    return false;
  }

  var _state$selection2 = state.selection,
      from = _state$selection2.from,
      to = _state$selection2.to;

  var getChar = function getChar(start, end) {
    return getTextContentFromSlice(TextSelection.create(state.doc, start, end).content());
  };

  for (var char = getChar(from - 1, from); char && !exclude.test(char); from--, char = getChar(from - 1, from)) {// Step backwards until reaching first excluded character or empty text content.
  }

  for (var _char = getChar(to, to + 1); _char && !exclude.test(_char); to++, _char = getChar(to, to + 1)) {// Step forwards until reaching the first excluded character or empty text content
  }

  if (from === to) {
    return false;
  }

  return {
    from: from,
    to: to
  };
};
/**
 * Retrieves the nearest space separated word from the current selection.
 *
 * @remarks
 *
 * This always expands outward so that given:
 * `The tw<start>o words<end>`
 * The selection would become
 * `The <start>two words<end>`
 *
 * In other words it expands until it meets an invalid character.
 *
 * @param state - the editor state
 *
 * @public
 */

var getSelectedWord = function getSelectedWord(state) {
  return getSelectedGroup(state, /[\s\0]/);
};
/**
 * Retrieve plugin state of specified type
 *
 * @param plugin - the plugin or plugin key
 * @param state - the editor state
 *
 * @public
 */

var getPluginState = function getPluginState(plugin, state) {
  return plugin.getState(state);
};
/**
 * Retrieve plugin meta data of specified type
 *
 * @param key - the plugin key
 * @param tr - the transaction to retrieve from
 *
 * @public
 */

var getPluginMeta = function getPluginMeta(key, tr) {
  return tr.getMeta(key);
};
/**
 * Set the plugin meta data
 *
 * @param key - the plugin key
 * @param tr - the transaction
 * @param data - the data to set
 *
 * @public
 */

var setPluginMeta = function setPluginMeta(key, tr, data) {
  return tr.setMeta(key, data);
};
/**
 * Get matching string from a list or single value
 *
 * @remarks
 * Get attrs can be called with a direct match string or array of string matches.
 * This method should be used to retrieve the required string.
 *
 * The index of the matched array used defaults to 0 but can be updated via the second parameter.
 *
 * @param match - the match(es)
 * @param index - the zero-index point from which to start
 *
 * @public
 */

var getMatchString = function getMatchString(match) {
  var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return Array.isArray(match) ? match[index] : match;
};
/**
 * Checks whether the passed value is a valid dom node
 *
 * @param domNode - the dom node
 *
 * @public
 */

var isDOMNode = function isDOMNode(domNode) {
  return isObject(Node) ? domNode instanceof Node : isObject(domNode) && isNumber(Cast(domNode).nodeType) && isString(Cast(domNode).nodeName);
};
/**
 * Checks for an element node like `<p>` or `<div>`.
 *
 * @param domNode - the dom node
 *
 * @public
 */

var isElementDOMNode = function isElementDOMNode(domNode) {
  return isDOMNode(domNode) && domNode.nodeType === Node.ELEMENT_NODE;
};
/**
 * Finds the closest element which matches the passed selector
 *
 * @param domNode - the dom node
 * @param selector - the selector
 *
 * @public
 */

var closestElement = function closestElement(domNode, selector) {
  if (!isElementDOMNode(domNode)) {
    return null;
  }

  if (!document.documentElement || !document.documentElement.contains(domNode)) {
    return null;
  }

  var matches = domNode.matches ? 'matches' : Cast('msMatchesSelector');

  do {
    if (domNode[matches] && domNode[matches](selector)) {
      return domNode;
    }

    domNode = domNode.parentElement || domNode.parentNode;
  } while (isElementDOMNode(domNode));

  return null;
};
/**
 * Checks for a text node.
 *
 * @param domNode - the dom node
 *
 * @public
 */

var isTextDOMNode = function isTextDOMNode(domNode) {
  return isDOMNode(domNode) && domNode.nodeType === Node.TEXT_NODE;
};
var getOffsetParent = function getOffsetParent(_ref3) {
  var view = _ref3.view,
      element = _ref3.element;
  return element ? element.offsetParent : view.dom.offsetParent;
};
/**
 * Retrieve the line height from a an element
 *
 * @param params - the element params
 */

var getLineHeight = function getLineHeight(_ref4) {
  var element = _ref4.element;
  return parseFloat(window.getComputedStyle(element, undefined).lineHeight || '');
};

/**
 * Retrieve the absolute coordinates
 *
 * @remarks
 *
 * We need to translate the co-ordinates because `coordsAtPos` returns co-ordinates
 * relative to `window`. And, also need to adjust the cursor container height.
 * (0, 0)
 *
 * ```
 * +--------------------- [window] ---------------------+
 * |   (left, top) +-------- [Offset Parent] --------+  |
 * | {coordsAtPos} | [Cursor]   <- cursorHeight      |  |
 * |               | [FloatingToolbar]               |  |
 * ```
 *
 * @param params - see {@link AbsoluteCoordinatesParams}.
 */
var absoluteCoordinates = function absoluteCoordinates(_ref5) {
  var view = _ref5.view,
      element = _ref5.element,
      position = _ref5.position,
      _ref5$cursorHeight = _ref5.cursorHeight,
      cursorHeight = _ref5$cursorHeight === void 0 ? getLineHeight({
    element: element
  }) : _ref5$cursorHeight;
  var offsetParent = getOffsetParent({
    view: view,
    element: element
  });
  var box = offsetParent.getBoundingClientRect();
  return {
    left: position.left - box.left,
    right: position.right - box.left,
    top: position.top - (box.top - cursorHeight) + offsetParent.scrollTop,
    bottom: box.height - (position.top - (box.top - cursorHeight) - offsetParent.scrollTop)
  };
};
/**
 * Retrieve the nearest non-text node
 *
 * @param domNode - the dom node
 */

var getNearestNonTextNode = function getNearestNonTextNode(domNode) {
  return isTextDOMNode(domNode) ? domNode.parentNode : domNode;
};
/**
 * Checks whether the cursor is at the end of the state.doc
 *
 * @param state - the editor state
 */

var atDocEnd = function atDocEnd(state) {
  return state.doc.nodeSize - state.selection.$to.pos - 2 === state.selection.$to.depth;
};
/**
 * Checks whether the cursor is at the beginning of the state.doc
 *
 * @param state - the editor state
 */

var atDocStart = function atDocStart(state) {
  return state.selection.$from.pos === state.selection.$from.depth;
};
/**
 * Get the start position of the parent of the current resolve position
 *
 * @param pmPosition - the resolved prosemirror position
 */

var startPositionOfParent = function startPositionOfParent(pmPosition) {
  return pmPosition.start(pmPosition.depth);
};
/**
 * Get the end position of the parent of the current resolve position
 *
 * @param pmPosition - the resolved prosemirror position
 *
 * @public
 */

var endPositionOfParent = function endPositionOfParent(pmPosition) {
  return pmPosition.end(pmPosition.depth) + 1;
};
/**
 * Retrieve the current position of the cursor
 *
 * @param selection - the editor selection
 * @returns a resolved position only when the selection is a text selection
 *
 * @public
 */

var getCursor = function getCursor(selection) {
  return isTextSelection(selection) ? selection.$cursor : undefined;
};
/**
 * Checks to see whether a nodeMatch checker is a tuple
 *
 * @remarks
 * A node matcher can either be a string, a function a regex or a regex tuple. This check for the latter two.
 *
 * @param nodeMatch - the node match
 *
 * @public
 */

var isRegexTuple = function isRegexTuple(nodeMatch) {
  return Array.isArray(nodeMatch) && nodeMatch.length > 0 && nodeMatch.length <= 2;
};
/**
 * Test the passed in regexp tuple
 *
 * @param tuple - a regex tuple
 * @param value - the string to test against
 *
 * @public
 */


var regexTest = function regexTest(tuple, value) {
  var regex = _construct(RegExp, _toConsumableArray(tuple));

  return regex.test(value);
};
/**
 * Checks to see whether the name of the passed node matches anything in the list provided.
 *
 * @param node - the prosemirror node
 * @param nodeMatches - the node matches array
 * @returns true if the node name is a match to any of the items in the nodeMatches array
 *
 * @public
 */


var nodeNameMatchesList = function nodeNameMatchesList(node, nodeMatches) {
  var outcome = false;

  if (!node) {
    return outcome;
  }

  var name = node.type.name;
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = nodeMatches[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var checker = _step.value;
      outcome = isRegexTuple(checker) ? regexTest(checker, name) : isFunction(checker) ? checker(name, node) : checker === name;

      if (outcome) {
        return outcome;
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return outcome;
};
/**
 * Checks whether a Prosemirror node is the top level `doc` node
 *
 * @param node - the prosemirror node
 * @param schema - the prosemirror schema
 *
 * @public
 */

var isDocNode = function isDocNode(node, schema) {
  return isProsemirrorNode(node) && (schema ? node.type === schema.nodes.doc : node.type.name === 'doc');
};
/**
 * Checks whether the passed in JSON is a valid object node
 *
 * @param value - the value to check
 *
 * @public
 */

var isObjectNode = function isObjectNode(value) {
  return isObject(value) && value.type === 'doc' && Array.isArray(value.content);
};

var fallbackContent = function fallbackContent(_ref6) {
  var fallback = _ref6.fallback,
      schema = _ref6.schema;
  return isProsemirrorNode(fallback) ? fallback : schema.nodeFromJSON(fallback);
};
/**
 * Creates a document node from the passed in content and schema.
 *
 * @param params - the destructured create document node params
 *
 * @public
 */


var createDocumentNode = function createDocumentNode(_ref7) {
  var content = _ref7.content,
      schema = _ref7.schema,
      doc = _ref7.doc,
      stringHandler = _ref7.stringHandler,
      _ref7$fallback = _ref7.fallback,
      fallback = _ref7$fallback === void 0 ? EMPTY_PARAGRAPH_NODE : _ref7$fallback;

  if (isProsemirrorNode(content)) {
    return content;
  }

  if (isObjectNode(content)) {
    try {
      return schema.nodeFromJSON(content);
    } catch (e) {
      console.error(e);
      return fallbackContent({
        fallback: fallback,
        schema: schema
      });
    }
  }

  if (isString(content) && stringHandler) {
    return stringHandler({
      doc: doc,
      content: content,
      schema: schema
    });
  }

  return fallbackContent({
    fallback: fallback,
    schema: schema
  });
};
/**
 * Checks which environment should be used. Returns true when we are in the dom environment.
 *
 * @param forceEnvironment - force a specific environment to override the outcome
 */

var shouldUseDOMEnvironment = function shouldUseDOMEnvironment(forceEnvironment) {
  return forceEnvironment === 'dom' || environment.isBrowser && !forceEnvironment;
};
/**
 * Retrieves the document based on the environment we are currently in.
 *
 * @param forceEnvironment - force a specific environment
 */

var getDocument = function getDocument(forceEnvironment) {
  return shouldUseDOMEnvironment(forceEnvironment) ? document : minDocument;
};

/**
 * Convert a node into its DOM representative
 *
 * @param params - the from node params
 *
 * @public
 */
var toDOM = function toDOM(_ref8) {
  var node = _ref8.node,
      schema = _ref8.schema,
      doc = _ref8.doc;
  var fragment = isDocNode(node, schema) ? node.content : Fragment.from(node);
  return DOMSerializer.fromSchema(schema).serializeFragment(fragment, {
    document: doc
  });
};

/**
 * Convert a prosemirror node into it's HTML contents
 *
 * @param params - the from node params
 *
 * @public
 */
var toHTML = function toHTML(_ref9) {
  var node = _ref9.node,
      schema = _ref9.schema,
      _ref9$doc = _ref9.doc,
      doc = _ref9$doc === void 0 ? getDocument() : _ref9$doc;
  var element = doc.createElement('div');
  element.appendChild(toDOM({
    node: node,
    schema: schema,
    doc: doc
  }));
  return element.innerHTML;
};

/**
 * Convert a HTML string into Prosemirror node
 *
 * @param params - the from html params
 *
 * @public
 */
var fromHTML = function fromHTML(_ref10) {
  var content = _ref10.content,
      schema = _ref10.schema,
      _ref10$doc = _ref10.doc,
      doc = _ref10$doc === void 0 ? getDocument() : _ref10$doc;
  var element = doc.createElement('div');
  element.innerHTML = content.trim();
  return DOMParser.fromSchema(schema).parse(element);
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Checks if the type a given `node` equals to a given `nodeType`.
 */
var nodeEqualsType = function nodeEqualsType(_ref) {
  var types = _ref.types,
      node = _ref.node;
  return node ? Array.isArray(types) && types.includes(node.type) || node.type === types : false;
};
/**
 * Creates a new transaction object from a given transaction
 *
 * @param tr - the prosemirror transaction
 *
 * @public
 */

var cloneTransaction = function cloneTransaction(tr) {
  return Object.assign(Object.create(tr), tr).setTime(Date.now());
};

/**
 * Returns a `delete` transaction that removes a node at a given position with
 * the given `node`. `position` should point at the position immediately before
 * the node.
 *
 * @param position - the prosemirror position
 *
 * @public
 */
var removeNodeAtPosition = function removeNodeAtPosition(_ref2) {
  var pos = _ref2.pos,
      tr = _ref2.tr;
  var node = tr.doc.nodeAt(pos);

  if (!node) {
    return tr;
  }

  return cloneTransaction(tr.delete(pos, pos + node.nodeSize));
};
/**
 * Returns DOM reference of a node at a given `position`.
 *
 * @remarks
 *
 * If the node type is of type `TEXT_NODE` it will return the reference of the
 * parent node.
 *
 * A simple use case
 *
 * ```ts
 * const element = findElementAtPosition($from.pos, view);
 * ```
 *
 * @param position - the prosemirror position
 * @param view - the editor view
 *
 * @public
 */

var findElementAtPosition = function findElementAtPosition(position, view) {
  var dom = view.domAtPos(position);
  var node = dom.node.childNodes[dom.offset];

  if (isTextDOMNode(dom.node)) {
    return dom.node.parentNode;
  }

  if (!node || isTextDOMNode(node)) {
    return dom.node;
  }

  return node;
};
/**
 * Returns a new transaction that deletes previous node.
 *
 * ```ts
 * dispatch(
 *    removeNodeBefore(state.tr)
 * );
 * ```
 *
 * @param tr
 *
 * @public
 */

var removeNodeBefore = function removeNodeBefore(tr) {
  var result = findPositionOfNodeBefore(tr.selection);

  if (result) {
    return removeNodeAtPosition({
      pos: result.pos,
      tr: tr
    });
  }

  return tr;
};
/**
 * Returns a new transaction that deletes the node after.
 *
 * ```ts
 * dispatch(
 *    removeNodeBefore(state.tr)
 * );
 * ```
 *
 * @param tr
 *
 * @public
 */

var removeNodeAfter = function removeNodeAfter(tr) {
  var result = findPositionOfNodeAfter(tr.selection);

  if (result) {
    return removeNodeAtPosition({
      pos: result.pos,
      tr: tr
    });
  }

  return tr;
};

/**
 * Returns a node of a given `nodeType` if it is selected. `start` points to the
 * start position of the node, `pos` points directly before the node.
 *
 * ```ts
 * const { extension, inlineExtension, bodiedExtension } = schema.nodes;
 *
 * const selectedNode = findSelectedNodeOfType({
 *   types: [extension, inlineExtension, bodiedExtension],
 *   selection,
 * });
 * ```
 */
var findSelectedNodeOfType = function findSelectedNodeOfType(_ref3) {
  var types = _ref3.types,
      selection = _ref3.selection;

  if (isNodeSelection(selection)) {
    var node = selection.node,
        $from = selection.$from;

    if (nodeEqualsType({
      types: types,
      node: node
    })) {
      return {
        node: node,
        pos: $from.pos,
        depth: $from.depth,
        start: $from.start(),
        end: $from.pos + node.nodeSize
      };
    }
  }

  return undefined;
};

/**
 * Iterates over parent nodes, returning the closest node and its start position
 * that the `predicate` returns truthy for. `start` points to the start position
 * of the node, `pos` points directly before the node.
 *
 * ```ts
 * const predicate = node => node.type === schema.nodes.blockquote;
 * const parent = findParentNode(predicate)(selection);
 * ```
 */
var findParentNode = function findParentNode(_ref4) {
  var predicate = _ref4.predicate,
      selection = _ref4.selection;
  var $from = selection.$from;

  for (var currentDepth = $from.depth; currentDepth > 0; currentDepth--) {
    var node = $from.node(currentDepth);

    if (predicate(node)) {
      var pos = currentDepth > 0 ? $from.before(currentDepth) : 0;
      var start = $from.start(currentDepth);
      var end = pos + node.nodeSize;
      return {
        pos: currentDepth > 0 ? $from.before(currentDepth) : 0,
        node: node,
        start: start,
        end: end
      };
    }
  }

  return;
};
/**
 * Finds the node at the passed selection.
 */

var findNodeAtSelection = function findNodeAtSelection(selection) {
  return findParentNode({
    predicate: function predicate() {
      return true;
    },
    selection: selection
  });
};
/**
 * Finds the node at the end of the Prosemirror document.
 *
 * @param doc - the parent doc node of the editor which contains all the other
 * nodes.
 *
 * @deprecated use `doc.lastChild` instead
 */

var findNodeAtEndOfDoc = function findNodeAtEndOfDoc(doc) {
  return findNodeAtPosition(Selection.atEnd(doc).$from);
};
/**
 * Finds the node at the start of the prosemirror.
 *
 * @param doc - the parent doc node of the editor which contains all the other
 * nodes.
 *
 * @deprecated use `doc.firstChild` instead
 */

var findNodeAtStartOfDoc = function findNodeAtStartOfDoc(doc) {
  return findNodeAtPosition(Selection.atStart(doc).$from);
};
/**
 * Finds the node at the resolved position.
 *
 * @param $pos - the resolve position in the document
 */

var findNodeAtPosition = function findNodeAtPosition($pos) {
  var depth = $pos.depth;
  var pos = depth > 0 ? $pos.before(depth) : 0;
  var node = $pos.node(depth);
  var start = $pos.start(depth);
  var end = pos + node.nodeSize;
  return {
    pos: pos,
    start: start,
    node: node,
    end: end
  };
};

/**
 *  Iterates over parent nodes, returning closest node of a given `nodeType`.
 *  `start` points to the start position of the node, `pos` points directly
 *  before the node.
 *
 *  ```ts
 *  const parent = findParentNodeOfType(schema.nodes.paragraph)(selection);
 *  ```
 */
var findParentNodeOfType = function findParentNodeOfType(_ref5) {
  var types = _ref5.types,
      selection = _ref5.selection;
  return findParentNode({
    predicate: function predicate(node) {
      return nodeEqualsType({
        types: types,
        node: node
      });
    },
    selection: selection
  });
};
/**
 * Returns position of the previous node.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */

var findPositionOfNodeBefore = function findPositionOfNodeBefore(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (!$pos) {
    throw new Error('Invalid value passed in.');
  }

  var nodeBefore = $pos.nodeBefore;
  var selection = Selection.findFrom($pos, -1);

  if (!selection || !nodeBefore) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeBefore.type,
    selection: selection
  });
  return parent ? parent : {
    node: nodeBefore,
    pos: nodeBefore.isLeaf ? selection.from : selection.from - 1,
    end: selection.from + nodeBefore.nodeSize,
    start: selection.from
  };
};
/**
 * Returns the position of the node after the current position, selection or state.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */

var findPositionOfNodeAfter = function findPositionOfNodeAfter(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (!$pos) {
    throw new Error('Invalid value passed in.');
  }

  var nodeAfter = $pos.nodeAfter;
  var selection = Selection.findFrom($pos, 1);

  if (!selection || !nodeAfter) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeAfter.type,
    selection: selection
  });
  return parent ? parent : {
    node: nodeAfter,
    pos: nodeAfter.isLeaf ? selection.from : selection.from - 1,
    end: selection.from + nodeAfter.nodeSize,
    start: selection.from
  };
};
/**
 * Checks whether the selection or state is currently empty.
 *
 * @param value - the current selection or state
 */

var selectionEmpty = function selectionEmpty(value) {
  return isSelection(value) ? value.empty : isEditorState(value) ? value.selection.empty : true;
};
/**
 * Check to see if a transaction has changed either the document or the current
 * selection.
 *
 * @param params - the TransactionChangeParams object
 */

var transactionChanged = function transactionChanged(tr) {
  return tr.docChanged || tr.selectionSet;
};

/**
 * Checks whether the node type passed in is active within the region. Used by
 * extensions to implement the `#active` method.
 *
 * To ignore attrs just leave the attrs object empty or undefined.
 *
 * "Borrowed" from [tiptap](https://github.com/scrumpy/tiptap)
 *
 * @param params - the destructured node active parameters
 */
var isNodeActive = function isNodeActive(_ref6) {
  var state = _ref6.state,
      type = _ref6.type,
      _ref6$attrs = _ref6.attrs,
      attrs = _ref6$attrs === void 0 ? Object.create(null) : _ref6$attrs;
  var selection = state.selection;

  var predicate = function predicate(node) {
    return node.type === type;
  };

  var parent = findSelectedNodeOfType({
    selection: selection,
    types: type
  }) || findParentNode({
    predicate: predicate,
    selection: selection
  });

  if (!Object.keys(attrs).length || !parent) {
    return bool(parent);
  }

  return parent.node.hasMarkup(type, attrs);
};

/**
 * Converts a schema to a simple json compatible object.
 */
var schemaToJSON = function schemaToJSON(schema) {
  var nodes = keys(schema.nodes).reduce(function (acc, key) {
    var spec = schema.nodes[key].spec;
    return _objectSpread({}, acc, _defineProperty({}, key, spec));
  }, Object.create(null));
  var marks = keys(schema.marks).reduce(function (acc, key) {
    var spec = schema.marks[key].spec;
    return _objectSpread({}, acc, _defineProperty({}, key, spec));
  }, Object.create(null));
  return {
    nodes: nodes,
    marks: marks
  };
};

/**
 * Update the selection with the provided MarkType
 *
 * @param type - the type to update the mark to
 * @param attrs - attrs to use for the mark
 *
 * @public
 */
var updateMark = function updateMark(_ref) {
  var type = _ref.type,
      _ref$attrs = _ref.attrs,
      attrs = _ref$attrs === void 0 ? Object.create(null) : _ref$attrs,
      appendText = _ref.appendText,
      range = _ref.range;
  return function (state, dispatch) {
    var selection = state.selection;
    var tr = state.tr;

    var _ref2 = range || selection,
        from = _ref2.from,
        to = _ref2.to;

    tr.addMark(from, to, type.create(attrs));

    if (appendText) {
      tr.insertText(appendText);
    }

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
};
/**
 * Toggle between wrapping an inactive node with the provided node type, and lifting it up into it's parent.
 *
 * @param type - the node type to toggle
 * @param attrs - the attrs to use for the node
 *
 * @public
 */

var toggleWrap = function toggleWrap(type, attrs) {
  return function (state, dispatch) {
    var isActive = isNodeActive({
      state: state,
      type: type
    });

    if (isActive) {
      return lift(state, dispatch);
    }

    return wrapIn(type, attrs)(state, dispatch);
  };
};
/**
 * Toggles a list item.
 *
 * @remarks
 * When the provided list wrapper is inactive (e.g. ul) then wrap the list with this type.
 * When it is active then remove the selected line from the list.
 *
 * @param type - the list node type
 * @param itemType - the list item type (must be in the schema)
 *
 * @public
 */

var toggleList = function toggleList(type, itemType) {
  return function (state, dispatch) {
    var isActive = isNodeActive({
      state: state,
      type: type
    });

    if (isActive) {
      return liftListItem(itemType)(state, dispatch);
    }

    return wrapInList(type)(state, dispatch);
  };
};

/**
 * Toggle a block between the provided type and toggleType.
 *
 * @param params - the destructured params
 *
 * @public
 */
var toggleBlockItem = function toggleBlockItem(_ref3) {
  var type = _ref3.type,
      toggleType = _ref3.toggleType,
      _ref3$attrs = _ref3.attrs,
      attrs = _ref3$attrs === void 0 ? Object.create(null) : _ref3$attrs;
  return function (state, dispatch) {
    var isActive = isNodeActive({
      state: state,
      type: type,
      attrs: attrs
    });

    if (isActive) {
      return setBlockType(toggleType)(state, dispatch);
    }

    return setBlockType(type, attrs)(state, dispatch);
  };
};

/**
 * A utility for calling option functions that may not exist
 */
var callMethod = function callMethod(_ref4, args) {
  var fn = _ref4.fn,
      defaultReturn = _ref4.defaultReturn;
  return isFunction(fn) ? fn.apply(void 0, _toConsumableArray(args)) : defaultReturn;
};
/**
 * Replaces text with an optional appended string at the end
 *
 * @param params - the destructured params
 *
 * @public
 */


var replaceText = function replaceText(_ref5) {
  var range = _ref5.range,
      type = _ref5.type,
      _ref5$attrs = _ref5.attrs,
      attrs = _ref5$attrs === void 0 ? Object.create(null) : _ref5$attrs,
      _ref5$appendText = _ref5.appendText,
      appendText = _ref5$appendText === void 0 ? '' : _ref5$appendText,
      _ref5$content = _ref5.content,
      content = _ref5$content === void 0 ? '' : _ref5$content,
      startTransaction = _ref5.startTransaction,
      endTransaction = _ref5.endTransaction;
  return function (state, dispatch) {
    var schema = state.schema,
        selection = state.selection; // const { $from, $to } = selection;

    var index = selection.$from.index();

    var _ref6 = range || selection,
        from = _ref6.from,
        to = _ref6.to; // Run the pre transaction hook


    var tr = callMethod({
      fn: startTransaction,
      defaultReturn: state.tr
    }, [state.tr, state]);

    if (isNodeType(type)) {
      if (!selection.$from.parent.canReplaceWith(index, index, type)) {
        return false;
      }

      tr.replaceWith(from, to, type.create(attrs, content ? schema.text(content) : undefined));
    } else {
      if (!content) {
        throw new Error('`replaceText` cannot be called without content when using a mark type');
      }

      tr.replaceWith(from, to, schema.text(content, isMarkType(type) ? [type.create(attrs)] : undefined));
    }
    /** Only append the text if when text is provided. */


    if (appendText) {
      tr.insertText(appendText);
    }

    if (dispatch) {
      if (isChrome(60)) {
        // A workaround for a chrome bug
        // https://github.com/ProseMirror/prosemirror/issues/710#issuecomment-338047650
        document.getSelection().empty();
      }

      dispatch(callMethod({
        fn: endTransaction,
        defaultReturn: tr
      }, [tr, state]));
    }

    return true;
  };
};

/**
 * Removes a mark from the current selection or provided from to
 *
 * @param params - the destructured params
 *
 * @public
 */
var removeMark = function removeMark(_ref7) {
  var type = _ref7.type,
      _ref7$expand = _ref7.expand,
      expand = _ref7$expand === void 0 ? false : _ref7$expand,
      range = _ref7.range,
      endTransaction = _ref7.endTransaction,
      startTransaction = _ref7.startTransaction;
  return function (state, dispatch) {
    var selection = state.selection;
    var tr = callMethod({
      fn: startTransaction,
      defaultReturn: state.tr
    }, [state.tr, state]);

    var _ref8 = range || selection,
        from = _ref8.from,
        to = _ref8.to;

    if (expand) {
      var _ref9 = range ? getMarkRange(state.doc.resolve(range.from), type) || isNumber(range.to) && getMarkRange(state.doc.resolve(range.to), type) || {
        from: from,
        to: to
      } : selectionEmpty(state) ? getMarkRange(state.selection.$anchor, type) || {
        from: from,
        to: to
      } : {
        from: from,
        to: to
      };

      from = _ref9.from;
      to = _ref9.to;
    }

    tr.removeMark(from, isNumber(to) ? to : from, type);

    if (dispatch) {
      dispatch(callMethod({
        fn: endTransaction,
        defaultReturn: tr
      }, [tr, state]));
    }

    return true;
  };
};
/**
 * Taken from https://stackoverflow.com/a/4900484
 *
 * Check that the browser is chrome. Supports passing a minimum version to check that it is a greater than or equal version.
 */

var isChrome = function isChrome() {
  var minVersion = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var parsedAgent = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
  return parsedAgent ? parseInt(parsedAgent[2], 10) >= minVersion : false;
};

/**
 * Keycode for ALT key.
 */

var ALT = ['Alt', '⌥'];
/**
 * Keycode for CTRL key.
 */

var CTRL = ['Control', '⌃'];
/**
 * Keycode for COMMAND/META key.
 */

var COMMAND = ['Meta', '⌘'];
/**
 * Keycode for SHIFT key.
 */

var SHIFT = ['Shift', '⇧'];
/**
 * Keycode for the windows key
 */

var WINDOWS = ['Windows', '', '❖'];
/**
 * Keycode and symbols for caps lock
 */

var CAPS_LOCK = ['CapsLock', '⇪', '⇪'];
/**
 * Shorthand names for common modifier key combinations which should `just` work on Apple and PC.
 */

var Modifier = {
  Primary: function Primary(isMac) {
    return isMac ? [COMMAND[0]] : [CTRL[0]];
  },
  PrimaryShift: function PrimaryShift(isMac) {
    return isMac ? [SHIFT[0], COMMAND[0]] : [CTRL[0], SHIFT[0]];
  },
  PrimaryAlt: function PrimaryAlt(isMac) {
    return isMac ? [ALT[0], COMMAND[0]] : [CTRL[0], ALT[0]];
  },
  Secondary: function Secondary(isMac) {
    return isMac ? [SHIFT[0], ALT[0], COMMAND[0]] : [CTRL[0], SHIFT[0], ALT[0]];
  },
  Access: function Access(isMac) {
    return isMac ? [CTRL[0], ALT[0]] : [SHIFT[0], ALT[0]];
  },
  Ctrl: function Ctrl() {
    return [CTRL[0]];
  },
  Alt: function Alt() {
    return [ALT[0]];
  },
  CtrlShift: function CtrlShift() {
    return [CTRL[0], SHIFT[0]];
  },
  Shift: function Shift() {
    return [SHIFT[0]];
  },
  ShiftAlt: function ShiftAlt() {
    return [SHIFT[0], ALT[0]];
  }
};
/**
 * The cross platform modifier key combination names.
 */

/**
 * Returns true if this is an apple environment either on the server or the client.
 */
var isApple = function isApple() {
  return environment.isApple;
};
/**
 * Create a consistent cross platform modifier string pattern.
 *
 * This can be used with the pressKeyWithModifier.
 *
 * ```ts
 * mod('Primary', 'A')
 * // => 'Control-A' // on PC
 * // => 'Cmd-A' // on Mac
 * ```
 *
 * @param modifier - A named modifier which is consistent across platforms
 *                  e.g. 'Primary' refers to 'Meta' on a mac and 'Control' on a PC
 * @param key - the key to press with the modifier. e.g. `Space` | `Enter`
 * @param [isApple] - a method which returns true when this is an apple device.
 */

var mod = function mod(modifier, key) {
  var isMacFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : isApple;

  switch (modifier) {
    case 'Primary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryShift':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryAlt':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Secondary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Access':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Ctrl':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Alt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'CtrlShift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Shift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'ShiftAlt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    default:
      throw new Error('Invalid modifier name passed in');
  }
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Flattens descendants of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const children = flatten(node);
 * ```
 */
var flatten = function flatten(_ref) {
  var node = _ref.node,
      _ref$descend = _ref.descend,
      descend = _ref$descend === void 0 ? true : _ref$descend;

  if (!isProsemirrorNode(node)) {
    throw new Error('Invalid "node" parameter');
  }

  var result = [];
  node.descendants(function (child, pos) {
    result.push({
      node: child,
      pos: pos
    });

    if (!descend) {
      return false;
    }

    return;
  });
  return result;
};

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate returns truthy for.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const textNodes = findChildren(node, child => child.isText, false);
 * ```
 */
var findChildren = function findChildren(_ref2) {
  var node = _ref2.node,
      predicate = _ref2.predicate,
      descend = _ref2.descend;

  if (!node) {
    throw new Error('Invalid "node" parameter');
  } else if (!predicate) {
    throw new Error('Invalid "predicate" parameter');
  }

  return flatten({
    node: node,
    descend: descend
  }).filter(function (child) {
    return predicate(child.node);
  });
};

var findNodeByPredicate = function findNodeByPredicate(_ref3) {
  var predicate = _ref3.predicate;
  return function (params) {
    return findChildren(_objectSpread$1({}, params, {
      predicate: predicate
    }));
  };
};
/**
 * Returns text nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const textNodes = findTextNodes(node);
 * ```
 */


var findTextNodes = findNodeByPredicate({
  predicate: function predicate(child) {
    return child.isText;
  }
});
/**
 * Returns inline nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const inlineNodes = findInlineNodes(node);
 * ```
 */

var findInlineNodes = findNodeByPredicate({
  predicate: function predicate(child) {
    return child.isInline;
  }
});
/**
 * Returns block descendants of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const blockNodes = findBlockNodes(node);
 * ```
 */

var findBlockNodes = findNodeByPredicate({
  predicate: function predicate(child) {
    return child.isBlock;
  }
});

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate returns truthy for.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const mergedCells = findChildrenByAttr(table, attrs => attrs.colspan === 2);
 * ```
 */
var findChildrenByAttr = function findChildrenByAttr(_ref4) {
  var node = _ref4.node,
      predicate = _ref4.predicate,
      descend = _ref4.descend;
  return findChildren({
    node: node,
    predicate: function (_predicate) {
      function predicate(_x) {
        return _predicate.apply(this, arguments);
      }

      predicate.toString = function () {
        return _predicate.toString();
      };

      return predicate;
    }(function (child) {
      return predicate(child.attrs);
    }),
    descend: descend
  });
};

/**
 * Iterates over descendants of a given `node`, returning child nodes of a given nodeType.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const cells = findChildrenByNode(table, schema.nodes.tableCell);
 * ```
 */
var findChildrenByNode = function findChildrenByNode(_ref5) {
  var node = _ref5.node,
      type = _ref5.type,
      descend = _ref5.descend;
  return findChildren({
    node: node,
    predicate: function predicate(child) {
      return child.type === type;
    },
    descend: descend
  });
};

/**
 * Iterates over descendants of a given `node`, returning child nodes that have a mark of a given markType.
 *
 * @remarks
 * It doesn't descend into a `node` when descend argument is `false` (defaults to `true`).
 *
 * ```ts
 * const nodes = findChildrenByMark(state.doc, schema.marks.strong);
 * ```
 */
var findChildrenByMark = function findChildrenByMark(_ref6) {
  var node = _ref6.node,
      type = _ref6.type,
      descend = _ref6.descend;
  return findChildren({
    node: node,
    predicate: function predicate(child) {
      return bool(type.isInSet(child.marks));
    },
    descend: descend
  });
};

/**
 * Returns `true` if a given node contains nodes of a given `nodeType`
 *
 * @remarks
 * ```ts
 * if (contains(panel, schema.nodes.listItem)) {
 *
 * }
 * ```
 */
var contains = function contains(_ref7) {
  var node = _ref7.node,
      type = _ref7.type;
  return findChildrenByNode({
    node: node,
    type: type
  }).length > 0;
};

/**
 * Creates an input rule based on the provided regex for the provided mark type
 */

var markInputRule = function markInputRule(_ref) {
  var regexp = _ref.regexp,
      type = _ref.type,
      getAttrs = _ref.getAttrs;
  return new InputRule(regexp, function (state, match, start, end) {
    var tr = state.tr;
    var attrs = isFunction(getAttrs) ? getAttrs(match) : getAttrs;
    var markEnd = end;

    if (match[1]) {
      var startSpaces = match[0].search(/\S/);
      var textStart = start + match[0].indexOf(match[1]);
      var textEnd = textStart + match[1].length;

      if (textEnd < end) {
        tr.delete(textEnd, end);
      }

      if (textStart > start) {
        tr.delete(start + startSpaces, textStart);
      }

      markEnd = start + startSpaces + match[1].length;
    }

    return tr.addMark(start, markEnd, type.create(attrs)) // Make sure not to continue with any ongoing marks
    .removeStoredMark(type);
  });
};
/**
 * Creates a paste rule based on the provided regex for the provided mark type
 */

var markPasteRule = function markPasteRule(_ref2) {
  var regexp = _ref2.regexp,
      type = _ref2.type,
      getAttrs = _ref2.getAttrs;

  var handler = function handler(fragment) {
    var nodes = [];
    fragment.forEach(function (child) {
      if (child.isText) {
        var text = child.text || '';
        var pos = 0; // ? For some reason including the index param makes this work. I'm not sure why...?

        findMatches(text, regexp).forEach(function (match, _) {
          if (!match[1]) {
            return;
          }

          var start = match.index;
          var end = start + match[0].length;
          var attrs = isFunction(getAttrs) ? getAttrs(match) : getAttrs;

          if (start > 0) {
            nodes.push(child.cut(pos, start));
          }

          nodes.push(child.cut(start, end).mark(type.create(attrs).addToSet(child.marks)));
          pos = end;
        });

        if (text && pos < text.length) {
          nodes.push(child.cut(pos));
        }
      } else {
        nodes.push(child.copy(handler(child.content)));
      }
    });
    return Fragment.fromArray(nodes);
  };

  return new Plugin({
    props: {
      transformPasted: function transformPasted(slice) {
        return new Slice(handler(slice.content), slice.openStart, slice.openEnd);
      }
    }
  });
};
/**
 * Creates an node input rule based on the provided regex for the provided node type
 */

var nodeInputRule = function nodeInputRule(_ref3) {
  var regexp = _ref3.regexp,
      type = _ref3.type,
      getAttrs = _ref3.getAttrs,
      _ref3$updateSelection = _ref3.updateSelection,
      updateSelection = _ref3$updateSelection === void 0 ? false : _ref3$updateSelection;
  return new InputRule(regexp, function (state, match, start, end) {
    var attrs = isFunction(getAttrs) ? getAttrs(match) : getAttrs;
    var tr = state.tr;
    tr.replaceWith(start - 1, end, type.create(attrs));

    if (updateSelection) {
      var $pos = tr.doc.resolve(start);
      tr.setSelection(new TextSelection($pos));
    }

    return tr;
  });
};
/**
 * Creates an node input rule based on the provided regex for the provided node type
 */

var plainInputRule = function plainInputRule(_ref4) {
  var regexp = _ref4.regexp,
      transformMatch = _ref4.transformMatch,
      _ref4$updateSelection = _ref4.updateSelection,
      updateSelection = _ref4$updateSelection === void 0 ? false : _ref4$updateSelection;
  return new InputRule(regexp, function (state, match, start, end) {
    var value = transformMatch(match);

    if (isNullOrUndefined(value)) {
      return null;
    }

    var tr = state.tr;

    if (value === '') {
      tr.delete(start, end);
    } else {
      tr.replaceWith(start, end, state.schema.text(value));
    }

    if (updateSelection) {
      var $pos = tr.doc.resolve(start);
      tr.setSelection(new TextSelection($pos));
    }

    return tr;
  });
};

export { ALT, CAPS_LOCK, COMMAND, CTRL, Modifier, SHIFT, WINDOWS, absoluteCoordinates, atDocEnd, atDocStart, canInsertNode, cloneTransaction, closestElement, contains, createDocumentNode, endPositionOfParent, environment, findBlockNodes, findChildren, findChildrenByAttr, findChildrenByMark, findChildrenByNode, findElementAtPosition, findInlineNodes, findNodeAtEndOfDoc, findNodeAtPosition, findNodeAtSelection, findNodeAtStartOfDoc, findParentNode, findParentNodeOfType, findPositionOfNodeAfter, findPositionOfNodeBefore, findSelectedNodeOfType, findTextNodes, flatten, fromHTML, getCursor, getDocument, getLineHeight, getMarkAttrs, getMarkRange, getMatchString, getNearestNonTextNode, getOffsetParent, getPluginMeta, getPluginState, getSelectedGroup, getSelectedWord, getTextContentFromSlice, isApple, isDOMNode, isDocNode, isDocNodeEmpty, isEditorSchema, isEditorState, isElementDOMNode, isEmptyParagraphNode, isMarkActive, isMarkType, isNodeActive, isNodeSelection, isNodeType, isObjectNode, isProsemirrorNode, isResolvedPos, isSelection, isTextDOMNode, isTextSelection, markInputRule, markPasteRule, mod, nodeEqualsType, nodeInputRule, nodeNameMatchesList, plainInputRule, removeMark, removeNodeAfter, removeNodeAtPosition, removeNodeBefore, replaceText, schemaToJSON, selectionEmpty, setPluginMeta, shouldUseDOMEnvironment, startPositionOfParent, toDOM, toHTML, toggleBlockItem, toggleList, toggleWrap, transactionChanged, updateMark };
//# sourceMappingURL=core-utils.esm.js.map
